import { newVariableGroup } from './variableGroups';

// Une adresse système arrive du back en cinq clés : l'agrégat historique
// (entity.ADDRESS, billed_customer.ADDRESS1…) et ses quatre parties. Seul
// l'agrégat est inséré dans le document — les contenus existants continuent
// donc de le résoudre sans réécriture — tandis que le formulaire n'expose que
// les parties, éditables séparément, et recompose l'agrégat à chaque saisie.
const PART_SUFFIXES = ['_STREET', '_COMPLEMENT', '_ZIPCODE', '_CITY'];

// `\d*` couvre billed_customer.ADDRESS1 ; exiger « .ADDRESS » avant le suffixe
// évite de prendre entity.REGISTRATION_CITY pour une partie d'adresse.
const AGGREGATE_KEY = /\.ADDRESS\d*$/;
const PART_KEY = /^(.*\.ADDRESS\d*)(_STREET|_COMPLEMENT|_ZIPCODE|_CITY)$/;

export const addressPartKeys = (aggregateKey) =>
  PART_SUFFIXES.map((suffix) => `${aggregateKey}${suffix}`);

// Clé agrégée d'une partie d'adresse, ou null si la clé n'en est pas une.
export const addressAggregateKey = (key) => {
  if (typeof key !== 'string') return null;
  const matched = key.match(PART_KEY);
  return matched ? matched[1] : null;
};

// Le complément est la seule partie facultative d'une adresse : vide, il n'est
// pas une valeur manquante.
const isOptionalAddressPart = (key) =>
  addressAggregateKey(key) !== null && key.endsWith('_COMPLEMENT');

// Saisie vide d'une variable texte. Une adresse découpée est vide dès qu'une
// partie obligatoire l'est : son agrégat recomposé, lui, n'est jamais vide
// tant qu'une seule partie est remplie.
const isTextValueEmpty = (variables, key, value) => {
  if (variables[key]?.derived) {
    return addressPartKeys(key).some((partKey) =>
      isTextValueEmpty(variables, partKey, variables[partKey]?.value));
  }
  if (isOptionalAddressPart(key)) return false;
  return !value;
};

// Saisie manquante d'une variable texte. Une variable marquée optionnelle
// dans le modèle (`optional`) ne manque jamais : vide, elle n'allume ni le
// « ! » du formulaire ni le filigrane.
export const isTextValueMissing = (variables, key, value = variables[key]?.value) =>
  !variables[key]?.optional && isTextValueEmpty(variables, key, value);

// Variable optionnelle restée vide : seulement signalée dans le formulaire.
export const isOptionalTextValueEmpty = (variables, key, value = variables[key]?.value) =>
  !!variables[key]?.optional && isTextValueEmpty(variables, key, value);

// Même mise en forme que full_address côté Rails : rue / complément / « CP ville ».
// Les parties vides sont écartées plutôt que rendues en ligne blanche.
export const composeAddress = (variables, aggregateKey) => {
  const [street, complement, zipcode, city] = addressPartKeys(aggregateKey).map(
    (key) => String(variables[key]?.value ?? '').trim()
  );
  const cityLine = [zipcode, city].filter(Boolean).join(' ');
  return [street, complement, cityLine].filter(Boolean).join('\n');
};

const addressLines = (text) =>
  String(text ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

// Comparaison à la forme près : lignes vides et espaces doublés n'en font pas
// une autre adresse (full_address du back commence par une ligne vide sans rue).
const sameAddress = (a, b) => {
  const normalize = (text) => addressLines(text).map((line) => line.replace(/\s+/g, ' ')).join('\n');
  return normalize(a) === normalize(b);
};

// Inverse de composeAddress pour un texte saisi d'un bloc : la dernière ligne
// « CP ville » donne code postal et ville, la première la rue, celles du milieu
// le complément, retours à la ligne compris — recomposé, le texte reste celui
// saisi. Sans ligne « CP ville », tout va dans rue et complément : rien n'est
// perdu, le code postal et la ville restent à compléter.
export const splitAddress = (text) => {
  const lines = addressLines(text);
  const cityLine = lines[lines.length - 1]?.match(/^(\d{4,5})\s+(.+)$/);
  const [street = '', ...complement] = cityLine ? lines.slice(0, -1) : lines;
  return {
    street,
    complement: complement.join('\n'),
    zipcode: cityLine?.[1] ?? '',
    city: cityLine?.[2] ?? '',
  };
};

// Adresse legacy : l'agrégat a été saisi d'un bloc (document antérieur au
// découpage, ou champ unique d'un groupement), ses parties ne le reflètent pas.
// Saisir une partie recompose l'agrégat, donc s'ils divergent, c'est lui qui a
// été saisi en dernier : on le redécoupe, sinon la saisie suivante d'une
// partie l'écraserait sans prévenir. Une fois découpé, il se recompose à
// l'identique — le passage suivant ne change plus rien.
// Pas de test d'usage : une adresse absente du document est rechargée depuis
// l'API au chargement (filterUselessVariables), elle correspond donc toujours à
// ses parties et le passage est sans effet.
const reconcileLegacyAddress = (variables, aggregateKey) => {
  const aggregate = variables[aggregateKey];
  if (addressLines(aggregate.value).length === 0) return;
  if (sameAddress(aggregate.value, composeAddress(variables, aggregateKey))) return;
  const parts = splitAddress(aggregate.value);
  const [street, complement, zipcode, city] = addressPartKeys(aggregateKey);
  variables[street].value = parts.street;
  variables[complement].value = parts.complement;
  variables[zipcode].value = parts.zipcode;
  variables[city].value = parts.city;
};

// Boxes d'un groupement du formulaire, avec les parties des adresses qu'il
// contient (cf. getVariablesGroupedByContainer) : sélectionner une partie
// désigne aussi le groupement.
export const withAddressParts = (variablesBoxes) =>
  variablesBoxes.flatMap((box) => [box, ...(box.addressParts ?? [])]);

// Une adresse n'est découpée que si le back envoie bien ses quatre parties :
// contacts multiples, mandataire (texte libre) et back non déployé gardent
// l'agrégat seul, donc leur ligne de formulaire d'origine.
const hasAddressParts = (variables, aggregateKey) =>
  addressPartKeys(aggregateKey).every((key) => variables[key]);

// Remplace la ligne « adresse » du formulaire par un bloc de quatre champs. Le
// groupement porte l'id de la clé agrégée : il hérite ainsi de sa position dans
// variableOrderArray, sans y toucher. L'agrégat est marqué `derived`, il n'a
// plus de ligne à lui (il est recomposé, jamais saisi).
export const attachAddressGroups = ({ variables, variableGroups }) => {
  Object.keys(variables).forEach((key) => {
    if (!AGGREGATE_KEY.test(key)) return;
    if (!hasAddressParts(variables, key)) {
      // Repli : `derived` a pu être enregistré dans le contenu à une époque où
      // le back envoyait les parties. Sans ce nettoyage, un retour arrière du
      // back masquerait la ligne adresse définitivement, sans rien pour la
      // remplacer.
      delete variables[key].derived;
      return;
    }
    variables[key].derived = true;
    reconcileLegacyAddress(variables, key);
    if (!variables[key].notUsed) {
      // Sans ça filterUselessVariables les écarterait au chargement suivant et
      // la valeur saisie dans une partie serait remplacée par celle de l'API.
      addressPartKeys(key).forEach((partKey) => {
        delete variables[partKey].notUsed;
      });
    }
    if (variableGroups.some((vG) => vG.id === key)) return;
    variableGroups.push({
      ...newVariableGroup({ id: key, name: variables[key].name }),
      variableIds: addressPartKeys(key),
    });
  });
};
